/* =====================================================================
 * Cookie-consent state — shared across B2C + enterprise hosts.
 *
 * Phase 12-5b lifted this hook out of `askyourai/src/hooks/useCookieConsent.ts`
 * so the shared `<ServiceTab />` (and the host-local `<CookieBanner />`
 * + `<OnboardingFlow />` that still live in B2C) can subscribe to a
 * single source of truth.
 *
 * Storage:
 *   - Key: `auw_cookie_consent`. Intentionally *not* tenant-scoped —
 *     a single browser profile keeps one set of GDPR preferences. Each
 *     tenant decides whether the surfaces that read this hook are
 *     visible at all (via `settings.consents.cookies` flag); when
 *     they're hidden the storage is dormant but harmless.
 *   - `useSyncExternalStore` driver so React 18 concurrent renders
 *     read a stable snapshot; cross-tab updates are surfaced via the
 *     in-process listener list (the storage `localStorage` event
 *     handler picks them up).
 *
 * GA4 dispatch:
 *   The B2C host ships GA4 (gated behind `analytics.ga4.enabled`).
 *   When the user changes consent, that host wants to forward the
 *   new categories to `gtag` Consent Mode v2. Enterprise tenants do
 *   NOT ship GA4. We expose `setCookieConsentSink()` so the host
 *   that *does* ship analytics can register a sink; this module
 *   stays free of host imports and the on-prem bundle's CDN-guard
 *   doesn't trip on a hidden gtag fetch.
 * ===================================================================== */

import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { COOKIE_CONSENT_VERSION } from './legalVersions';

const STORAGE_KEY = 'auw_cookie_consent';

export interface CookieCategories {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

export interface StoredCookieConsent {
  version: string;
  agreed_at: string;
  categories: CookieCategories;
}

const DEFAULT_CATEGORIES: CookieCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
};

/* ---- analytics sink (DI) ---- */

export type CookieConsentSink = (
  categories: Pick<CookieCategories, 'analytics' | 'marketing'>,
) => void;

let _sink: CookieConsentSink = () => {
  /* default no-op — enterprise hosts ship no analytics integration */
};

export function setCookieConsentSink(sink: CookieConsentSink | null): void {
  _sink = typeof sink === 'function' ? sink : () => {};
}

/* ---- global cookie settings dialog event ---- */

let cookieSettingsListeners: Array<() => void> = [];

export function openCookieSettings(): void {
  for (const listener of cookieSettingsListeners) {
    listener();
  }
}

export function subscribeOpenCookieSettings(listener: () => void): () => void {
  cookieSettingsListeners.push(listener);
  return () => {
    cookieSettingsListeners = cookieSettingsListeners.filter((entry) => entry !== listener);
  };
}

function dispatchCookieConsent(categories: CookieCategories): void {
  try {
    _sink({
      analytics: categories.analytics,
      marketing: categories.marketing,
    });
  } catch {
    /* sink failures (e.g. gtag missing) must not break the consent
     * write — categories are already persisted. */
  }
}

/* ---- snapshot cache (useSyncExternalStore needs referential stability) ---- */

let cachedRaw: string | null = null;
let cachedSnapshot: StoredCookieConsent | null = null;

function readStored(): StoredCookieConsent | null {
  try {
    const raw =
      typeof window === 'undefined'
        ? null
        : window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredCookieConsent;
    if (!parsed.categories) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getSnapshot(): StoredCookieConsent | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = raw ? readStored() : null;
  return cachedSnapshot;
}

let listeners: Array<() => void> = [];

function emitChange(): void {
  if (typeof window === 'undefined') return;
  cachedRaw = window.localStorage.getItem(STORAGE_KEY);
  cachedSnapshot = cachedRaw ? readStored() : null;
  for (const fn of listeners) fn();
}

function writeStored(consent: StoredCookieConsent): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    /* localStorage write blocked — keep emitting in-memory change */
  }
  emitChange();
}

function subscribe(cb: () => void): () => void {
  listeners.push(cb);
  return () => {
    listeners = listeners.filter((fn) => fn !== cb);
  };
}

function buildStoredConsent(
  categories: CookieCategories,
): StoredCookieConsent {
  return {
    version: COOKIE_CONSENT_VERSION,
    agreed_at: new Date().toISOString(),
    categories,
  };
}

/* ---- public hook ---- */

export interface CookieConsentApi {
  hasConsented: () => boolean;
  categories: CookieCategories;
  acceptAll: () => void;
  acceptSelected: (selected: Partial<CookieCategories>) => void;
  rejectOptional: () => void;
}

export function useCookieConsent(): CookieConsentApi {
  const stored = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const hasConsented = useCallback((): boolean => {
    const s = stored ?? readStored();
    return s?.version === COOKIE_CONSENT_VERSION;
  }, [stored]);

  const categories = useMemo<CookieCategories>(() => {
    const s = stored ?? readStored();
    if (s?.version === COOKIE_CONSENT_VERSION && s.categories) {
      return { ...s.categories, necessary: true };
    }
    return { ...DEFAULT_CATEGORIES };
  }, [stored]);

  const acceptAll = useCallback((): void => {
    const consent = buildStoredConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
    writeStored(consent);
    dispatchCookieConsent(consent.categories);
  }, []);

  const acceptSelected = useCallback(
    (selected: Partial<CookieCategories>): void => {
      const cats: CookieCategories = {
        necessary: true,
        analytics: selected.analytics ?? false,
        marketing: selected.marketing ?? false,
      };
      const consent = buildStoredConsent(cats);
      writeStored(consent);
      dispatchCookieConsent(cats);
    },
    [],
  );

  const rejectOptional = useCallback((): void => {
    const consent = buildStoredConsent({ ...DEFAULT_CATEGORIES });
    writeStored(consent);
    dispatchCookieConsent(consent.categories);
  }, []);

  return { hasConsented, categories, acceptAll, acceptSelected, rejectOptional };
}

/**
 * Replay the persisted consent state to the registered sink at boot.
 * The B2C host calls this from `main.tsx` after `setCookieConsentSink()`
 * so the GA4 Consent Mode reflects an earlier session's choices on
 * first paint. Enterprise hosts do not call this (no sink registered).
 */
export function restoreCookieConsentToSink(): void {
  const stored = readStored();
  if (
    stored?.version === COOKIE_CONSENT_VERSION &&
    stored.categories &&
    stored.categories.analytics
  ) {
    dispatchCookieConsent(stored.categories);
  }
}
